import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import moment from 'moment';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import errorHandler from '../../../support_func/errorHandler';
import selectStyles from '../../../support_func/selectStyles';

const HistoricalExportModal = ({
  show,
  onHide,
  idri,
  token,
  sensorOptions,
  actuatorOptions,
}) => {
  const { t } = useTranslation();

  const config = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }), [token]);

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [exportConfig, setExportConfig] = useState(null);

  const [selectedSensors, setSelectedSensors] = useState([]);
  const [selectedActuators, setSelectedActuators] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);

  const [startDate, endDate] = dateRange;

  useEffect(() => {
    if (!show) return;

    setError(null);
    setSuccess(null);

    axios
      .get(`${process.env.REACT_APP_API_SRT}/v2/historicalexports/config`, config)
      .then((res) => {
        if (res.data.code === '0') {
          setExportConfig(res.data);
        } else {
          setError(t('problem'));
        }
      })
      .catch((error) => {
        errorHandler(error, setIsPending, setError);
      });
  }, [show, config, t]);

  const totalSelected = selectedSensors.length + selectedActuators.length;

  const maxElements = exportConfig?.max_elements || 30;
  const maxDays = exportConfig?.max_days || 90;

  const selectedDays =
    startDate && endDate
      ? moment(endDate).startOf('day').diff(moment(startDate).startOf('day'), 'days')
      : 0;

  const canSubmit =
    startDate &&
    endDate &&
    totalSelected > 0 &&
    totalSelected <= maxElements &&
    selectedDays <= maxDays &&
    !isPending;

  const submitExport = async (e) => {
    e.preventDefault();

    setError(null);
    setSuccess(null);

    if (!idri?.ghatorid) {
      setError(t('problem'));
      return;
    }

    if (totalSelected === 0) {
      setError(t('missingOrEmptyParameters'));
      return;
    }

    if (totalSelected > maxElements) {
      setError(`${t('tooManyObjects')}: ${maxElements}`);
      return;
    }

    if (!startDate || !endDate) {
      setError(t('invalidDates'));
      return;
    }

    if (moment(endDate).isBefore(moment(startDate), 'day')) {
      setError(t('invalidDates'));
      return;
    }

    if (selectedDays > maxDays) {
      setError(`${t('dateRangeTooLong')}: ${maxDays}`);
      return;
    }

    setIsPending(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_SRT}/v2/historicalexports`,
        {
          ididri: idri.ghatorid,
          sensor_ids: selectedSensors.map((s) => s.value),
          actuator_ids: selectedActuators.map((a) => a.value),
          start_date: moment(startDate).format('YYYY-MM-DD'),
          end_date: moment(endDate).format('YYYY-MM-DD'),
        },
        config
      );

      if (res.data.code === '0') {
        setSuccess(t('historicalExportRequested'));
      } else {
        setError(t(`error${res.data.msg_code}`));
      }
    } catch (error) {
      errorHandler(error, setIsPending, setError);
    } finally {
      setIsPending(false);
    }
  };

  const clearForm = () => {
    setSelectedSensors([]);
    setSelectedActuators([]);
    setDateRange([null, null]);
    setError(null);
    setSuccess(null);
  };

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('historicalExport')}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={submitExport}>
        <Modal.Body>
          <Row>
            <Col xs={12} md={6}>
              <strong>{t('sensores')}</strong>
              <Select
                isMulti
                menuShouldScrollIntoView
                placeholder={t('sensores')}
                value={selectedSensors}
                onChange={(value) => setSelectedSensors(value || [])}
                options={sensorOptions}
                styles={selectStyles}
              />
            </Col>

            <Col xs={12} md={6} className='mt-3 mt-md-0'>
              <strong>{t('actuadores')}</strong>
              <Select
                isMulti
                menuShouldScrollIntoView
                placeholder={t('actuadores')}
                value={selectedActuators}
                onChange={(value) => setSelectedActuators(value || [])}
                options={actuatorOptions}
                styles={selectStyles}
              />
            </Col>

            <Col xs={12} className='mt-3'>
              <strong>{t('dateRange')}</strong>
              <DatePicker
                required
                locale={localStorage.getItem('language')}
                calendarStartDay={1}
                customInput={<Form.Control className='valueInput' />}
                dateFormat='dd/MM/yyyy'
                selectsRange
                startDate={startDate}
                endDate={endDate}
                onChange={(dates) => setDateRange(dates)}
                maxDate={new Date()}
                withPortal
                showYearDropdown
                showMonthDropdown
              />
            </Col>

            <Col xs={12} className='mt-3'>
              <small>
                {t('maxElements')}: {maxElements}. {t('maxDays')}: {maxDays}.
              </small>
            </Col>

            {error && (
              <Col xs={12} className='mt-3 text-danger'>
                {error}
              </Col>
            )}

            {success && (
              <Col xs={12} className='mt-3 text-success'>
                {success}
              </Col>
            )}
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant='light' onClick={clearForm} disabled={isPending}>
            {t('clean')}
          </Button>
          <Button variant='secondary' onClick={onHide} disabled={isPending}>
            {t('cerrar')}
          </Button>
          <Button type='submit' className='dark' disabled={!canSubmit}>
            {isPending ? `${t('comprobando')}...` : t('requestExport')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default HistoricalExportModal;